import { store } from '../../web-legacy/appStore';
import { AlchemySolutionModel, BcqrExperimentType, ContentType } from '../../web-legacy/models/apiModels';
import { AMCSignedInUserLink, SourceApps } from '../../web-legacy/models/constants/constants';
import { IAppState } from '../../web-legacy/models/reduxState';
import { appendQueryParams } from './getHelputils';
import { getString } from './stringLoader';
import { isCommunityContent } from './contentTypeUtils';

export const getIsAMCContained = (firstThreeSolutions: AlchemySolutionModel[]): boolean => {
  // Check if first three solutions contain AMC/LMC solution.
  return firstThreeSolutions.some((solution: AlchemySolutionModel) => isCommunityContent(solution.ContentType));
};

export const getNewSolutionsWithAMC = (
  firstThreeSolutions: AlchemySolutionModel[],
  restSolutions: AlchemySolutionModel[]
): AlchemySolutionModel[] => {
  restSolutions.some((solution: AlchemySolutionModel) => {
    // Find if there's AMC/LMC solution in the rest, promote the first AMC/LMC solution to the 3rd place
    if (isCommunityContent(solution.ContentType)) {
      firstThreeSolutions.splice(2, 1, solution);
      return true;
    }
  });
  return firstThreeSolutions;
};

export const getBcqrSolutions = (
  bcqrExperimentType: BcqrExperimentType,
  solutions: AlchemySolutionModel[]
): AlchemySolutionModel[] => {
  const firstThreeSolutions = solutions.slice(0, 3);
  const restSolutions = solutions.slice(3);
  switch (bcqrExperimentType) {
    case BcqrExperimentType.Control:
      // Remove all AMC/LMC contents
      let hasContentTypeDefined = false;
      const result = solutions.filter((solution: AlchemySolutionModel) => {
        if (!hasContentTypeDefined && !!solution.ContentType) {
          hasContentTypeDefined = true;
        }
        return !isCommunityContent(solution.ContentType);
      });
      // Check to make sure Alchemy is returning ContentType
      if (!hasContentTypeDefined && result.length === 0) {
        return solutions.slice(0, 3);
      }
      return !!result ? result.slice(0, 3) : [];
    case BcqrExperimentType.Experiment1:
      return firstThreeSolutions;
    case BcqrExperimentType.Experiment2:
      if (getIsAMCContained(firstThreeSolutions)) {
        // Check if first three solutions contain AMC solution. If yes, return as it is.
        return firstThreeSolutions;
      } else {
        // If no, promote the first AMC solution to the 3rd place from the rest.
        return getNewSolutionsWithAMC(firstThreeSolutions, restSolutions);
      }
  }
};

export const getIsDifferentContentType = (solutions: AlchemySolutionModel[]): boolean => {
  const contentType: ContentType = solutions[0]?.ContentType;
  let isDifferent = false;
  const firstThreeSolutions: AlchemySolutionModel[] = solutions.slice(0, 3);
  isDifferent = firstThreeSolutions.some((solution: AlchemySolutionModel) => {
    // If find the contentType is different from previous one, return
    return solution.ContentType !== contentType;
  });
  return isDifferent;
};

// remove '- Microsoft Support' and '- Microsoft Community' from Alchemy solution title
export const removeBcqrSolutionTitleSuffix = (title: string, market: string): string => {
  if (!title) {
    return '';
  }
  const contactSupportRegex = /^.*- Microsoft Support$/;
  const communityAnswersRegex = /^.*- Microsoft Community$/;
  const englishLocales = /^en.*$/i;
  if (englishLocales.test(market)) {
    if (contactSupportRegex.test(title)) {
      return title.split(' - Microsoft Support')[0];
    } else if (communityAnswersRegex.test(title)) {
      return title.split(' - Microsoft Community')[0];
    }
  }
  return title;
};

// get banner text based on content type
export const getBannerText = (contentType: ContentType): string => {
  // Currently only AMC/LMC/SMC ContentTypes are returnd
  return isCommunityContent(contentType)
    ? getString('result_communityAnswersString')
    : getString('result_microsoftSupportString');
};

export const getBCQRTitle = (): string => getString('result_moreHelp');

export const getIsControl = (bcqrExperimentType: BcqrExperimentType): boolean => {
  return bcqrExperimentType === BcqrExperimentType.Control;
};

// append source and correlationId to the links as requested by MAGIC team
export const appendArticleUrl = (
  uri: string,
  correlationId: string,
  isControl: boolean,
  isSignedIn: boolean,
  userEmail: string,
  contentType: ContentType,
  market: string,
  isGetHelp: boolean
): string => {

  // append from=surfaceappbcqr to the url if the source app is SurfaceApp and the content type is AMC
  const appState: IAppState = store.getState();
  const sourceApp =  appState.GlobalState.sourceApp;
  if((sourceApp === SourceApps.SurfaceApp || sourceApp === SourceApps.SurfaceApp2) && isCommunityContent(contentType)) {
    const url = new URL(uri);
    url.searchParams.append('from', 'surfaceappbcqr');
    return url.toString();
  }

  if (isGetHelp) {
    return appendQueryParams(uri);
  }
  const communityAnswerSignedInLink = AMCSignedInUserLink.replace('{market}', market);
  if (isSignedIn) {
    return isControl
      ? uri
      : isCommunityContent(contentType)
      ? `${communityAnswerSignedInLink}?login_hint=${userEmail}&from=ContactUsWebBCQR&CorrelationId=${correlationId}&url=${uri}`
      : uri;
  }
  return isControl
    ? uri
    : isCommunityContent(contentType)
    ? `${uri}?from=ContactUsWebBCQR&CorrelationId=${correlationId}`
    : uri;
};
